// View 9: Order Confirmation

import { useEffect } from 'react';
import { useOrderStore } from '@/stores/orderStore';
import { track } from '@/utils/analytics';

export function OrderConfirmationView() {
  const { selectedServices, address, personalInfo, confirmationId, electricityCompletionLink } = useOrderStore();

  // Check which services were selected
  const hasWater = selectedServices.some((s) => s.type === 'water');
  const hasInternet = selectedServices.some((s) => s.type === 'internet');
  const hasElectricity = selectedServices.some((s) => s.type === 'electricity');

  useEffect(() => {
    const serviceTypes = selectedServices.map(s => s.type);
    const { onitSelected, onitPlan } = useOrderStore.getState();
    track('checkout_order_complete', {
      confirmation_id: confirmationId,
      zip: address?.zip,
      city: address?.city,
      state: address?.stateCode,
      flow_type: useOrderStore.getState().flowType,
      has_water: hasWater,
      has_electricity: hasElectricity,
      has_internet: hasInternet,
      services: serviceTypes,
      total_services: serviceTypes.length,
      rent_or_own: personalInfo?.rentOrOwn,
      onit_selected: onitSelected,
      onit_available: !!onitPlan,
      source: 'confirmation_view',
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Format address for display
  const formattedAddress = address
    ? `${address.street || address.fullAddress}, ${address.city}, ${address.stateCode}`
    : '—';

  return (
    <section className="view" style={{ display: 'block' }}>
      <div className="container">
        {/* Success Header */}
        <div className="confirmation-header text--center mb-6">
          <div className="confirmation-checkmark mb-4">
            <svg className="checkmark-icon" viewBox="0 0 52 52" xmlns="http://www.w3.org/2000/svg">
              <circle className="checkmark-circle" cx="26" cy="26" r="25" fill="none" />
              <path className="checkmark-check" fill="none" d="M14.1 27.2l7.1 7.2 16.7-16.8" />
            </svg>
          </div>
          <h1 className="mb-3">Order Confirmed!</h1>
          <p className="confirmation-subtitle">Thank you for choosing 2TurnItOn</p>
        </div>

        {/* Confirmation ID */}
        <div className="confirmation-card mb-5">
          <div className="confirmation-card__header">
            <h2>Confirmation Details</h2>
          </div>
          <div className="confirmation-card__body">
            <div className="confirmation-detail">
              <span className="confirmation-detail__label">Confirmation Number:</span>
              <span className="confirmation-detail__value" id="confirmation-number">
                {confirmationId || '—'}
              </span>
            </div>
            <div className="confirmation-detail">
              <span className="confirmation-detail__label">Service Address:</span>
              <span className="confirmation-detail__value" id="confirmation-address">
                {formattedAddress}
              </span>
            </div>
            <div className="confirmation-detail">
              <span className="confirmation-detail__label">Move Date:</span>
              <span className="confirmation-detail__value" id="confirmation-move-date">
                {personalInfo?.moveDate || '—'}
              </span>
            </div>
          </div>
        </div>

        {/* Next Steps */}
        <div className="next-steps mb-6">
          <h2 className="text--center mb-5">Next Steps</h2>

          {/* Water Service - Success Message */}
          {hasWater && (
            <div className="service-next-step" id="water-next-step">
              <div className="service-next-step__icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M12 2.69l5.66 5.66a8 8 0 1 1-11.31 0z" />
                </svg>
              </div>
              <div className="service-next-step__content">
                <h3>Water Service</h3>
                <p className="service-next-step__status service-next-step__status--success">
                  ✓ Your water service request has been received. If we need any additional information, a representative will contact you.
                </p>
              </div>
            </div>
          )}

          {/* Internet Service - Concierge Message */}
          {hasInternet && (
            <div className="service-next-step" id="internet-next-step">
              <div className="service-next-step__icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="2" />
                  <path d="M16.2 7.8a5 5 0 0 1 0 8.4M7.8 16.2a5 5 0 0 1 0-8.4M20.5 5.5a10 10 0 0 1 0 13M3.5 18.5a10 10 0 0 1 0-13" />
                </svg>
              </div>
              <div className="service-next-step__content">
                <h3>Internet Service</h3>
                <p className="service-next-step__notice">
                  <strong>Concierge Service by 2TurnItOn (owned by ComparePower)</strong>
                </p>
                <p>A representative will reach out to handle disclosures and hunt for the best deals for your needs.</p>
                <div className="service-next-step__alternative">
                  <strong>Or call us at:</strong>
                  <a href="tel:1-888-300-6961" className="phone-link">1-888-300-6961</a>
                </div>
              </div>
            </div>
          )}

          {/* Electricity Service - ComparePower Message */}
          {hasElectricity && (
            <div className="service-next-step" id="electricity-next-step">
              <div className="service-next-step__icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" />
                </svg>
              </div>
              <div className="service-next-step__content">
                <h3>Electricity Service</h3>
                <p className="service-next-step__notice">
                  <strong>Action Required:</strong> {electricityCompletionLink
                    ? 'Click below to complete your electricity setup with ComparePower.'
                    : 'Check your email for a link to complete your electricity setup with ComparePower.'}
                </p>
                {electricityCompletionLink && (
                  <a
                    href={electricityCompletionLink}
                    id="electricity-completion-link"
                    className="btn btn--primary btn--large mt-3"
                    target="_blank"
                    rel="noopener"
                  >
                    Complete Electricity Order
                  </a>
                )}

                <div className="trust-context">
                  <p className="text--small mb-2"><strong>Why ComparePower?</strong></p>
                  <p className="text--small m-0">
                    ComparePower was the first to show consumers that TX electricity plans meaningfully change based on your actual usage.
                    We help you avoid "gotchas" in the fine print to ensure you get the best rate.
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Email Confirmation Notice */}
        <div className="info-notice mb-6">
          <svg className="info-notice__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
            <polyline points="22,6 12,13 2,6" />
          </svg>
          <div className="info-notice__content">
            <p className="info-notice__title">Check Your Email</p>
            <p className="info-notice__text">
              A confirmation email with all your order details has been sent to{' '}
              <span id="confirmation-email">{personalInfo?.email || 'your email address'}</span>.
            </p>
          </div>
        </div>
      </div>
    </section>
  )
}
